// apps/frontend/src/features/experiential/lib/defusionPlayer.ts
// ACT-style defusion playback: the same clip re-spoken by manipulation —
// slowed to a crawl, sped to a squeak, or drifting far away (muffled + quiet).

export type DefusionVariant = "plain" | "slow" | "fast" | "far";

const VARIANTS: Record<DefusionVariant, { rate: number; lowpassHz?: number; gain: number }> = {
  plain: { rate: 1.0, gain: 1.0 },
  slow: { rate: 0.55, gain: 1.0 },
  fast: { rate: 1.7, gain: 1.0 },
  far: { rate: 1.0, lowpassHz: 700, gain: 0.4 },
};

export function createDefusionPlayer() {
  let ctx: AudioContext | null = null;
  let buffer: AudioBuffer | null = null;
  let source: AudioBufferSourceNode | undefined;
  let closed = false;
  let lastVariant: DefusionVariant | null = null;

  function ensureCtx(): AudioContext {
    if (!ctx) ctx = new AudioContext();
    return ctx;
  }

  async function load(data: ArrayBuffer) {
    buffer = await ensureCtx().decodeAudioData(data);
  }

  async function play(variant: DefusionVariant) {
    if (!buffer) throw new Error("defusion player: play before load");
    const c = ensureCtx();
    if (c.state === "suspended") await c.resume();
    stop();
    const v = VARIANTS[variant];
    const s = c.createBufferSource();
    s.buffer = buffer;
    s.playbackRate.value = v.rate;
    const gain = c.createGain();
    gain.gain.value = v.gain;
    if (v.lowpassHz) {
      const filter = c.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = v.lowpassHz;
      s.connect(filter);
      filter.connect(gain);
    } else {
      s.connect(gain);
    }
    gain.connect(c.destination);
    s.start();
    source = s;
    lastVariant = variant;
  }

  function stop() {
    try { source?.stop(); } catch { /* already stopped */ }
    source = undefined;
  }

  async function close() {
    stop();
    if (ctx) { await ctx.close(); closed = true; }
  }

  return {
    load, play, stop, close,
    // test-only inspectors
    _debugLastVariant: () => lastVariant,
    _debugClosed: () => closed,
  };
}
